import { useQuery } from '@apollo/client';
import { faBell } from '@fortawesome/pro-regular-svg-icons';
import { faUserCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  Body,
  Button,
  CustomNavElementLocation,
  NavBar,
  NavBarData,
  NavItemData,
  palettes
} from '@indigo-ag/component-module';
import { AuthService, Logger } from '@indigo-ag/ui-core';
import i18next from 'core/i18n';
import React, { useContext, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { DefaultRoute, UserContext } from 'core/components/GateWay/GateWay';
import { getNotificationCount } from 'features/general/queries';
import { IconWithBadge, MOBILE_BREAK_POINT, NavItem, Permissions, Scope, environmentIndicator, utils } from 'shared';
import { BREAKPOINT, useBreakpoint } from 'shared/hooks/breakpoint';
import { heapUtil, text } from 'shared/utils';

// Custom type for our necessities.
type ItemData = NavItemData & {
  active?: boolean;
  path?: string;
  scope?: Scope;
  permission?: string | Array<string>;
  hideForPermissions?: string[];
  items?: ItemData[];
  action?: () => void;
  mobileOnly?: boolean;
};
interface NavBarDataCustom extends NavBarData {
  navigationItems: ItemData[];
  contextItems: ItemData[];
}

const handleSignOutClick = () => {
  AuthService.clearTokens();
  sessionStorage.removeItem('usersProfile');
  heapUtil.track('financing-admin/nav/logout');

  // TODO Figure out how to clear login from external loginService
  window.location.href = '/';
};

const navigation: NavBarDataCustom = {
  navigationItems: [
    {
      active: false,
      id: 'br-growers',
      label: i18next.t('contracts'),
      path: '/br-growers',
      hideForPermissions: [Permissions.FA_SCOPE_ALL],
      scope: Scope.BR
    },
    // Only visible to GLOBAL Scope
    {
      id: 'growers_menu',
      items: [
        {
          active: false,
          id: 'growers_menu_2',
          label: i18next.t('br'),
          path: '/br-growers',
          scope: Scope.BR
        }
      ],
      label: i18next.t('appNavDrawer_growers'),
      scope: Scope.GLOBAL
    },
    {
      active: false,
      id: 'cessions',
      label: text.firstLetterUppercase(i18next.t('cessions')),
      path: '/cessions',
      hideForPermissions: [
        Permissions.FA_EXTERNAL_LEGAL,
        Permissions.FA_EXTERNAL_CREDIT,
        Permissions.FA_EXTERNAL_FIELD_MONITORING,
        Permissions.FA_SECRETARY
      ],
      scope: Scope.BR
    },
    {
      active: false,
      id: 'reports_menu',
      label: i18next.t('appNavDrawer_reports'),
      permission: [Permissions.FA_INTERNAL],
      hideForPermissions: [Permissions.FA_SECRETARY],
      items: [
        {
          active: false,
          id: 'reports_menu_3',
          label: i18next.t('creditLeadReports'),
          path: '/credit-lead-report',
          scope: Scope.BR,
          permission: Permissions.FA_CREDIT_LEAD
        }
      ]
    },
    {
      active: false,
      id: 'terramagna_report_item',
      label: text.firstLetterUppercase(i18next.t('report')),
      path: '/terramagna-report',
      scope: Scope.BR,
      permission: Permissions.FA_EXTERNAL_FIELD_MONITORING,
      hideForPermissions: [Permissions.FA_SCOPE_ALL]
    },
    {
      id: 'tools_menu',
      items: [
        {
          active: false,
          id: 'tools_menu_1',
          label: i18next.t('appNavDrawer_users'),
          path: '/user-management',
          scope: Scope.GLOBAL
        },
        {
          active: false,
          id: 'salesforce-poll',
          label: 'Salesforce Poll',
          path: '/salesforce-poll',
          scope: Scope.GLOBAL
        },
        {
          active: false,
          id: 'campaigns-management',
          label: utils.text.firstLetterUppercase(i18next.t('campaigns')),
          path: '/campaigns-management',
          scope: Scope.GLOBAL
        },
        {
          active: false,
          id: 'tools_menu_2',
          label: i18next.t('uploadGrowerData'),
          path: '/grower-report-upload',
          scope: Scope.BR,
          permission: Permissions.FA_ADMIN
        }
      ],
      label: i18next.t('tools'),
      scope: Scope.BR,
      permission: Permissions.FA_ADMIN
    },
    {
      active: false,
      id: 'pricing-management',
      label: utils.text.toTitleCase(i18next.t('pricingManagement')),
      path: '/pricing-management',
      scope: Scope.BR,
      permission: [
        Permissions.FA_INTERNAL,
        Permissions.FA_CREDIT_LEAD,
        Permissions.FA_EXTERNAL_CREDIT,
        Permissions.FA_EXTERNAL_LEGAL
      ],
      hideForPermissions: [Permissions.FA_SECRETARY]
    }
  ],
  contextItems: []
};

const FixedNavBar = styled(NavBar)<{ marginTop: string }>`
  position: fixed;
  width: 100%;
  z-index: 100;
  top: 0;
  margin-top: ${props => props.marginTop};
`;

export const AppNav: React.FunctionComponent = () => {
  /**
   * Context Role
   */
  const userContext = useContext(UserContext);

  /**
   * State
   */
  const [selectedScope, setSelectedScope] = useState<string>(userContext.scope || Scope.GLOBAL);

  /**
   * Breakpoint hook
   */
  const breakpoint = useBreakpoint();

  /**
   * Query
   */
  const { data: financingAdminNotificationsData, error, stopPolling } = useQuery<{
    financingAdminNotifications: number;
  }>(getNotificationCount, {
    pollInterval: 30000
  });
  if (error) {
    Logger.warn('Encountered error during financingAdminNotifications polling. Stopping poll requests.');
    stopPolling();
  }
  const activeConversationsCount = financingAdminNotificationsData?.financingAdminNotifications || 0;

  /**
   * History & Pathname
   */
  const history = useHistory();
  const { pathname } = useLocation();
  const notificationsRoute = '/notifications';

  const handleCustomScopeChange = () => {
    setSelectedScope(Scope.BR);
    history.push(DefaultRoute.BR);
  };

  const renderCustomNavElements = (location: CustomNavElementLocation) => {
    const brButtonText = `🇧🇷 ${i18next.t('br')}`;
    if (location === CustomNavElementLocation.BEFORE_NAVIGATION_MENUS) {
      return (
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Body style={{ color: palettes.neutral.white }}>Ag Financing Admin</Body>
            {userContext.isSuper ? (
              <Button variant="tertiary" onClick={handleCustomScopeChange}>
                {brButtonText}
              </Button>
            ) : null}
          </div>
        </div>
      );
    }

    if (location === CustomNavElementLocation.AFTER_NAVIGATION_MENUS) {
      return activeConversationsCount > 0 ? (
        <NavItem
          data-test="go-to-notifications"
          active={pathname.includes(notificationsRoute)}
          path={notificationsRoute}
        >
          <IconWithBadge icon={faBell} badge={activeConversationsCount.toString()} />
        </NavItem>
      ) : null;
    }

    return null;
  };

  const onNavigate = (url: string) => {
    heapUtil.track(`financing-admin/nav${url}`);

    history.push(url);
  };

  const canSeeRoute = (scope?: string, permission?: string | Array<string>) => {
    return scope
      ? (scope === selectedScope || selectedScope === Scope.GLOBAL) &&
          (permission ? userContext.hasPermission(permission) || userContext.isAdmin : true)
      : permission
      ? userContext.hasPermission(permission) || userContext.isAdmin
      : true;
  };

  const mobileOnlyFilter = (item: ItemData): boolean => (!!item.mobileOnly ? breakpoint === BREAKPOINT.SM : true);

  return (
    <FixedNavBar
      title="Application"
      mobileBreakpoint={MOBILE_BREAK_POINT}
      data={{
        ...navigation,
        navigationItems: navigation.navigationItems
          .filter(n =>
            n.hideForPermissions && n.hideForPermissions.some(p => userContext.hasPermission(p))
              ? false
              : canSeeRoute(n.scope, n.permission)
          )
          .filter(mobileOnlyFilter)
          .map(navItem => {
            return {
              ...navItem,
              active: navItem.path ? pathname.includes(navItem.path) : false,
              items: navItem.items
                ? navItem.items
                    .filter(m => canSeeRoute(m.scope, m.permission))
                    .filter(mobileOnlyFilter)
                    .map(i => ({ ...i, active: i.path ? pathname.includes(i.path) : false }))
                : undefined
            };
          }),
        contextItems: [
          {
            icon: faUserCircle,
            id: 'user_menu',
            badge:
              breakpoint === BREAKPOINT.SM
                ? activeConversationsCount > 0
                  ? activeConversationsCount.toString()
                  : undefined
                : undefined,
            items: [
              {
                path: notificationsRoute,
                id: 'notifications_mobile',
                label: i18next.t('notifications'),
                mobileOnly: true,
                badge: activeConversationsCount > 0 ? activeConversationsCount.toString() : undefined
              },
              {
                action: handleSignOutClick,
                id: 'user_menu_1',
                label: i18next.t('appNavDrawer_signOut')
              }
            ].filter(mobileOnlyFilter),
            label: userContext.fullName || i18next.t('user')
          }
        ] as ItemData[]
      }}
      contentPadding="40px"
      contentMaxWidth=""
      onNavigate={onNavigate}
      renderCustomNavElements={renderCustomNavElements}
      marginTop={environmentIndicator === 'staging' ? '30px' : '0px'}
    />
  );
};
